import CMSLayout from "@/layout/CMSLayout.tsx";
import {useTranslation} from "react-i18next";
import { AuthenticationForm, AuthenticationListForm, AuthenticationFormProps } from "@/components/cms/AuthenticationForm.tsx";
import {FormProvider, useForm} from "react-hook-form";
import {handleSubmitMultilingualForm, useGetArtworkAuthenticationFormData} from "@/api/cms.ts";
import {DoubleMultilingualField} from "@/components/form-items/MultilingualField.tsx";
import {Switch} from "@/components/form-items/AbstractForm.tsx";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import { constructLocaleDict, getMainLanguage, multilingualFieldSchema, SupportedLocale } from "@/i18n.ts";
import {cmsPageSchema, artworkAuthenticationContentSchema } from "@/lib/cms/cms-types.ts";
import { Separator } from "../../components/ui/separator";
import { Input } from "../../components/ui/input";
import React, { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/context/auth/AuthContext";
import api from "@/lib/axiosConfig.ts";
import { useToast } from "@/hooks/use-toast";
import InfoBulle from "@/components/ui/info-bulle.tsx";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";

const artworkAuthenticationFormSchema = multilingualFieldSchema(
  cmsPageSchema
    .pick({
      published: true
    })
    .merge(
      artworkAuthenticationContentSchema.omit({
        id: true,
      }),
    )
    .optional(),
);

type ArtworkAuthenticationFormData = z.infer<typeof artworkAuthenticationFormSchema>;

const ArtworkAutheticationCMS = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [homePageFile, setHomePageFile] = useState<File | string | null>(null);
  const [sidebarFile, setSidebarFile] = useState<File | string | null>(null);
  const { data: artworkAuthenticationData } = useGetArtworkAuthenticationFormData(instanceData?.CMS?.id ?? "");
  const [doPost, setDoPost] = useState(false);

  const activeTab = searchParams.get("tab") ?? "form-content";

  const artworkData = useMemo(() => {
    const datas: Record<SupportedLocale, any> = {fr: {} as any, en: {} as any, es: {} as any, nl: {} as any}
    if (artworkAuthenticationData) {
      const dataToFormat = artworkAuthenticationData

      Object.entries(dataToFormat).forEach(([key, value]) => {
        if (key !== 'homePageFileUrl' && key !== 'sideBarFileUrl') {
          const locale = (key as string).slice(-2).toLowerCase() as SupportedLocale;
          datas[locale][key.slice(0, -2)] = value;
        }
      });
      setDoPost(false);
    } else {
      setDoPost(true);
    }
    return datas;
  }, [artworkAuthenticationData]);

  const defaultValues: ArtworkAuthenticationFormData = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const exp = artworkData?.[lang as SupportedLocale];
        return {
          published: exp?.published ?? false,
          pageName: exp?.pageName ?? "Artwork Authentication",
          title: exp?.title ?? "",
          content: exp?.content ?? "",
          honorDeclaration: exp?.honorDeclaration ?? "",
          certificationLegalStatement: exp?.certificationLegalStatement ?? "",
          sidebarTitle: exp?.sidebarTitle ?? "",
        };
      }),
    [artworkData],
  );

  const form = useForm<ArtworkAuthenticationFormData>({
    resolver: zodResolver(artworkAuthenticationFormSchema),
    values: defaultValues,
  });

  React.useEffect(() => {
    if (!artworkAuthenticationData) return;
    setHomePageFile(artworkAuthenticationData.homePageFileUrl ?? null);
    setSidebarFile(artworkAuthenticationData.sideBarFileUrl ?? null);
  }, [artworkAuthenticationData]);

  // Log des erreurs de validation
  React.useEffect(() => {
  }, [form.formState.errors, form.formState.isValid]);

  const onSubmit = async (submittedData: ArtworkAuthenticationFormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    const formData = new FormData();
    Object.entries(submittedData).forEach(([key, value]) => {
      Object.entries(value).forEach(([fieldKey, fieldValue]) => {
        if (fieldKey !== 'homePageFile' && fieldKey !== 'sidebarFile') {
          formData.append(`${fieldKey}${key.toUpperCase()}`, fieldValue as string);
        }
      });
    });
    formData.append('homePageFile', (homePageFile as File) ?? null);
    formData.append('sideBarFile', (sidebarFile as File) ?? null);

    try {
      if (doPost) {
        formData.append('cmsId', instanceData?.CMS?.id ?? "");
        await api.post('/back/api/cms/artwork-authentication-cms', formData);
        setDoPost(false);
        toast({
          title: t("success"),
          description: t("homepageSuccessMessage"),
          variant: "success",
        });
      } else {
        await api.patch(`/back/api/cms/artwork-authentication-cms/${instanceData?.CMS?.id}`, formData);
        toast({
          title: t("success"),
          description: t("homepageSuccessMessage"),
          variant: "success",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: t("error"),
        description: t("homepageErrorMessage"),
        variant: "error",
      });
    }
  };

  const tabList = useMemo(
    () =>
      [
        {
          label:
          artworkAuthenticationData?.[getMainLanguage()]?.pageName ??
            t("cms.authentication.tabs.content"),
          component: (_props: AuthenticationFormProps) => {
            return (
              <form
                onSubmit={handleSubmitMultilingualForm(form, artworkData, onSubmit)}
                className="flex flex-col w-full gap-4"
                id="artwork-authentication"
              >
                <div className="flex flex-col">
                  <div className="flex flex-col gap-1">
                    <p className="text-[#0c111d] leading-[1.5rem] text-lg font-semibold font-['Raleway'] leading-normal">
                      {t("cms.authentication.photos.title")}
                    </p>
                    <p className="relative text-[0.875rem] leading-[1.5rem] font-medium font-raleway text-darkslategray text-left">
                      {t("cms.authentication.photos.subtitle1")}
                    </p>
                  </div>

                  <div className="flex gap-4 mt-4">
                    {!homePageFile ? <Input
                        type="file"
                        id="homePageFile"
                        label={t("cms.artwork-authentication.photos.homePage")}
                        name="fr.homePageFile"
                        accept=".jpg"
                        onChange={(event) => setHomePageFile(event.target?.files?.[0] ?? null)}
                      />
                    :
                      <div className="flex flex-col gap-2 w-full">
                        <p className="relative text-[0.875rem] leading-[1.5rem] font-medium font-raleway text-darkslategray text-left">
                          {t("cms.artwork-authentication.photos.homePage")}
                        </p>
                        <div className="relative w-full h-[200px] border-2 border-slate-300 rounded-xl overflow-hidden group">
                          <img
                            src={homePageFile instanceof File ? URL.createObjectURL(homePageFile) : homePageFile}
                            alt="file"
                            className="w-full h-full object-cover object-center"
                          />
                          {/* Overlay au survol */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          {/* Bouton supprimer centré */}
                          <button
                            type="button"
                            onClick={() => setHomePageFile(null)}
                            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-3 bg-transparent flex gap-2 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg"
                          >
                            <Trash2 className="w-5 h-5" />
                            <p>Supprimer</p>
                          </button>
                        </div>
                        <InfoBulle  message={t("cms.authentication.homepagefile.message") } />
                      </div>
                    }
                    {!sidebarFile ? <Input
                        type="file"
                        id="sidebarFile"
                        label={t("cms.artwork-authentication.photos.sidebar")}
                        name="fr.sidebarFile"
                        accept=".jpg"
                        onChange={(event) => setSidebarFile(event.target?.files?.[0] ?? null)}
                      /> :
                      <div className="flex flex-col gap-2 w-full">
                        <p
                          className="relative text-[0.875rem] leading-[1.5rem] font-medium font-raleway text-darkslategray text-left">
                          {t("cms.artwork-authentication.photos.sidebar")}
                        </p>
                        <div className="relative w-full h-[200px] border-2 border-slate-300 rounded-xl overflow-hidden group">
                          <img
                            src={sidebarFile instanceof File ? URL.createObjectURL(sidebarFile) : sidebarFile}
                            alt="file"
                            className="w-full h-full object-cover object-center"
                          />
                          <div
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          <button
                            type="button"
                            onClick={() => setSidebarFile(null)}
                            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-3 bg-transparent flex gap-2 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg"
                          >
                            <Trash2 className="w-5 h-5" />
                            <p>Supprimer</p>
                          </button>
                        </div>
                      <InfoBulle  message={t("cms.authentication.sidebarfile.message") } />
                      </div>
                    }
                  </div>

                  <Separator
                    orientation="horizontal"
                    className="w-full h-[0.5px] bg-gray-300"
                  />
                  <div className="flex flex-col gap-4 mt-4">
                    <p className="text-[#0c111d] leading-[1.5rem] text-lg font-semibold font-['Raleway'] leading-normal">
                      {t("cms.authentication.content.title")}
                    </p>
                    <p className="relative text-[0.875rem] leading-[1.5rem] font-medium font-raleway text-darkslategray text-left">
                      {t("cms.authentication.content.subtitle")}
                    </p>
                  </div>

                  <div className="flex flex-row gap-4">
                    <DoubleMultilingualField
                      className="w-full"
                      corner={(lang) => (
                        <Switch dispLabel={false} name={`${lang}.published`} />
                      )}
                      render={(lang) => <AuthenticationForm language={lang} instanceId={instanceData?.id ?? ''} />}
                    />
                  </div>
                </div>
              </form>
            );
          },
          value: "form-content",
          publishedSwitch: "published",
        },
        {
          label:
          artworkAuthenticationData?.[getMainLanguage()]?.content.creditsTitle ??
            t("cms.authentication.tabs.list"),
          component: AuthenticationListForm,
          value: "form-list",
          publishedSwitch: "publishedList",
        }
      ] satisfies {
        label: string;
        value: string;
        publishedSwitch: string;
        component: (props: AuthenticationFormProps) => JSX.Element;
      }[],
    [t, artworkAuthenticationData, homePageFile, sidebarFile],
  );


  return (
    <CMSLayout
      pageName={t("cms.authentication.formTitle")}
      formIds={["artwork-authentication"]}
    >
      <div className="justify-start items-center gap-4 block w-full">

        <FormProvider {...form}>
          <Tabs className="w-full" defaultValue={activeTab}>
            <TabsList>
              {tabList.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabList.map((tab) => {
              const Comp = tab.component;
              return (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="inline-flex gap-8 w-full data-[state=inactive]:hidden flex-col"
                >
                  <Comp language={getMainLanguage() as SupportedLocale} instanceId={instanceData?.id ?? ''} />
                  {/* <DoubleMultilingualField
                    corner={(lang) => (
                      <Switch
                        dispLabel={false}
                        name={`${lang}.${tab.publishedSwitch}`}
                      />
                    )}
                    render={(lang) => <Comp language={lang} />}
                  /> */}
                </TabsContent>
              );
            })}
          </Tabs>
        </FormProvider>
      </div>

    </CMSLayout>
  )

}

export default ArtworkAutheticationCMS;
